import React, { useState, useEffect } from 'react';
import { Dialog, Snackbar, Alert } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useBooking } from '../../contexts/BookingContext';
import { useModal } from '../../contexts/ModalContext';
import OtherChargesSection from './OtherCharges';
import { numberToWords } from '../../utils/helper';

// ─── Design tokens (matches BookingDetailPage) ────────────────────────────────
const T = {
  bg:       '#f2f6f3',
  white:    '#ffffff',
  ink:      '#0c1810',
  ink2:     '#1e3328',
  subtle:   '#4d6b58',
  muted:    '#8ba898',
  border:   '#dae6df',
  borderL:  '#eaf2ec',
  green:    '#0a6b3e',
  greenMd:  '#0d8a50',
  greenLt:  '#e4f5ec',
  greenSf:  'rgba(10,107,62,0.07)',
  amber:    '#d97706',
  amberLt:  '#fef3c7',
  amberBdr: '#fcd34d',
  red:      '#dc2626',
  redLt:    '#fee2e2',
  redBdr:   '#fca5a5',
  blue:     '#1d4ed8',
  blueLt:   '#dbeafe',
  blueBdr:  '#93c5fd',
};


// ─── formatPriceVerbose (original) ────────────────────────────────────────────
const formatPriceVerbose = (value) => {
  if (!value || isNaN(value)) return '';
  const num = parseInt(value);
  return `₹${num.toLocaleString('en-IN')}`;
};

// ─── Design primitives ────────────────────────────────────────────────────────

/** Labelled section block with icon. Pass hideHeader to skip the title row (e.g. when child has its own title). */
function Section({ icon, title, accent, children, hideHeader }) {
  return (
    <div style={{
      background: accent
        ? 'linear-gradient(135deg,rgba(10,107,62,0.05),rgba(10,107,62,0.09))'
        : T.white,
      border: `1px solid ${accent ? '#c2e8d0' : T.border}`,
      borderRadius: 12,
      padding: '15px 16px',
      display: 'flex',
      flexDirection: 'column',
      gap: 14,
    }}>
      {!hideHeader && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>{icon}</span>
          <span style={{ fontSize: 13, fontWeight: 800, color: T.ink, letterSpacing: '-0.2px' }}>{title}</span>
        </div>
      )}
      {children}
    </div>
  );
}

/** Floating-label style text input */
function Field({ label, required, children, helper, error }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: T.subtle, letterSpacing: '0.2px', display: 'flex', alignItems: 'center', gap: 4 }}>
        {label}
        {required && <span style={{ color: T.red }}>*</span>}
      </div>
      {children}
      {error  && <div style={{ fontSize: 11, color: T.red,   fontWeight: 600, marginTop: 1 }}>{error}</div>}
      {helper && !error && <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>{helper}</div>}
    </div>
  );
}

/** Styled number / text input */
function Input({ name, value, onChange, type = 'text', placeholder, disabled, step, min, max, suffix }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        name={name} value={value} onChange={onChange}
        type={type} placeholder={placeholder}
        disabled={disabled} step={step} min={min} max={max}
        onFocus={() => setFocused(true)}
        onBlur={()  => setFocused(false)}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: suffix ? '9px 96px 9px 12px' : '9px 12px',
          fontSize: 13, color: disabled ? T.muted : T.ink,
          background: disabled ? '#eff4f1' : T.white,
          border: `1.5px solid ${focused ? T.green : T.border}`,
          borderRadius: 9, outline: 'none',
          cursor: disabled ? 'not-allowed' : 'text',
          transition: 'border-color 150ms, box-shadow 150ms',
          boxShadow: focused ? '0 0 0 3px rgba(10,107,62,0.1)' : 'none',
        }}
      />
      {suffix && (
        <span style={{
          position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)',
          fontSize: 11, fontWeight: 700, color: T.green, pointerEvents: 'none',
          maxWidth: 88, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {suffix}
        </span>
      )}
    </div>
  );
}

/** Booking / Token amount row — % + amount side by side */
function AmountPair({ icon, title, pctName, pctVal, amtName, amtVal, onChange, pctErr, amtErr, amtHelper, onInfo }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <span style={{ fontSize: 15 }}>{icon}</span>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: T.ink2 }}>{title}</span>
        <button
          type="button"
          onClick={onInfo}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', color: T.muted, fontSize: 15, lineHeight: 1, display: 'flex', alignItems: 'center' }}
          title="More info"
        >ℹ</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="% of Base Price" error={pctErr}>
          <Input
            name={pctName} value={pctVal} onChange={onChange}
            type="number" placeholder="0" step={1} min={0} max={100}
          />
        </Field>
        <Field label="Amount (₹)" required error={amtErr} helper={!amtVal && !amtErr ? amtHelper : undefined}>
          <Input
            name={amtName} value={amtVal} onChange={onChange}
            type="number" placeholder="0" step={100} min={0}
            suffix={amtVal ? formatPriceVerbose(amtVal) : ''}
          />
        </Field>
      </div>
    </div>
  );
}

/** Summary row */
function SumRow({ label, value, bold, green, italic }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: `1px solid ${T.borderL}` }}>
      <span style={{ fontSize: italic ? 11 : 12.5, color: green ? T.green : T.subtle, fontWeight: bold ? 700 : 500, fontStyle: italic ? 'italic' : 'normal' }}>{label}</span>
      <span style={{ fontSize: bold ? 14 : 12.5, fontWeight: bold ? 800 : 600, color: green ? T.green : T.ink2, letterSpacing: bold ? '-0.3px' : 0 }}>{value || '—'}</span>
    </div>
  );
}

/** Info tooltip dialog (replaces MUI Dialog for the nested one) */
function InfoModal({ title, content, onClose }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1300,
      background: 'rgba(10,24,16,0.45)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        background: T.white, borderRadius: 14, width: '100%', maxWidth: 420,
        padding: '22px 24px',
        boxShadow: '0 16px 60px rgba(0,0,0,0.22)',
        border: `1.5px solid ${T.border}`,
      }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: T.ink, marginBottom: 12 }}>{title}</div>
        <div style={{ fontSize: 13, color: T.subtle, lineHeight: 1.75, whiteSpace: 'pre-line' }}>{content}</div>
        <button
          onClick={onClose}
          style={{
            marginTop: 18, background: 'linear-gradient(135deg,#0a6b3e,#0d8a50)',
            border: 'none', borderRadius: 8, padding: '7px 20px',
            fontSize: 12.5, fontWeight: 700, color: '#fff', cursor: 'pointer',
          }}
        >Got it</button>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const AddBookingFinance = ({ open, onClose, booking, onSave, otherCharges = [], isEdit = false }) => {
  // ── State (identical to original) ────────────────────────────────────────
  const [formData, setFormData] = useState({
    booking_date: null,
    base_price: booking?.unit?.base_price || '',
    booking_amount: '',
    booking_amount_pct: '',
    token_amount: '',
    token_amount_pct: '',
    tax: 0,
    taxes: 0,
    other_charges: [],
    total_price: '',
    subtotal: '',
    notes: '',
  });

  const [errors, setErrors]       = useState({});
  const [loading, setLoading]     = useState(false);
  const [snackbar, setSnackbar]   = useState({ open: false, message: '', severity: 'success' });
  const [infoDialog, setInfoDialog] = useState({ open: false, title: '', content: '' });

  const { updateBookingAmount }           = useBooking();
  const { openSuccessModal, openErrorModal } = useModal();

  // ── useEffect: init form (booking.sale_order holds amounts when present) ───
  useEffect(() => {
    if (booking) {
      const so = booking.sale_order || {};
      const basePrice        = Number(so.base_price ?? booking.unit?.base_price) || 0;
      const bookingAmount    = Number(so.booking_amount ?? booking.booking_amount) || 0;
      const tokenAmount      = Number(so.token_amount ?? booking.token_amount) || 0;
      const bookingAmountPct =
        basePrice > 0 && bookingAmount > 0 ? ((bookingAmount / basePrice) * 100).toFixed(1) : '';
      const tokenAmountPct =
        bookingAmount > 0 && tokenAmount > 0 ? ((tokenAmount / bookingAmount) * 100).toFixed(1) : '';

      setFormData({
        booking_date:       booking.booking_date ? (dayjs(booking.booking_date).isValid() ? dayjs(booking.booking_date) : null) : null,
        base_price:         basePrice.toString(),
        booking_amount:     bookingAmount > 0 ? bookingAmount.toString() : '',
        booking_amount_pct: bookingAmountPct,
        token_amount:       tokenAmount > 0 ? tokenAmount.toString() : '',
        token_amount_pct:   tokenAmountPct,
        other_charges:      so.other_charges || booking.other_charges || [],
        total_price:        so.total_price ?? booking.total_price ?? '',
        subtotal:           so.subtotal ?? booking.subtotal ?? '',
        notes:              booking.notes ?? so.notes ?? '',
        tax: 0, taxes: 0,
      });
    }
  }, [booking]);

  // ── useEffect: sync unit base_price (original) ────────────────────────────
  useEffect(() => {
    if (booking?.unit?.base_price != null) {
      setFormData(f => ({ ...f, base_price: booking.unit.base_price }));
    }
  }, [booking?.unit]);

  // ── useEffect: recalculate totals (original) ──────────────────────────────
  useEffect(() => {
    const basePrice    = Math.round(Number(formData.base_price)) || 0;
    const otherTotal   = formData.other_charges.reduce((s, c) => s + (Math.round(Number(c.amount)) || 0), 0);
    const tax          = Math.round(Number(formData.tax)) || 0;
    const subtotal     = basePrice + otherTotal;
    const taxAmount    = Math.round((subtotal * tax) / 100);
    const total        = subtotal + taxAmount;
    setFormData(f => ({ ...f, taxes: taxAmount, subtotal, total_price: total }));
  }, [formData.base_price, formData.tax, formData.other_charges, formData.booking_amount, formData.token_amount]);

  // ── handleChange — full bidirectional logic (original) ────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'booking_amount_pct') {
      if (value === '') {
        setFormData(f => ({ ...f, booking_amount_pct: '', booking_amount: '' }));
        if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
        return;
      }
      const bp  = Math.round(Number(formData.base_price)) || 0;
      const pct = Number(value);
      const safePct = Number.isFinite(pct) ? pct : 0;
      const amt = Math.round((safePct / 100) * bp);
      setFormData(f => ({
        ...f,
        booking_amount_pct: value,
        booking_amount: amt > 0 ? amt.toString() : ''
      }));

    } else if (name === 'booking_amount') {
      if (value === '') {
        setFormData(f => ({ ...f, booking_amount: '', booking_amount_pct: '' }));
        if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
        return;
      }
      const bp  = Math.round(Number(formData.base_price)) || 0;
      const amt = Math.round(Number(value)) || 0;
      const pct = bp > 0 && amt > 0 ? ((amt / bp) * 100).toFixed(1) : '';
      setFormData(f => ({
        ...f,
        booking_amount: value,
        booking_amount_pct: pct,
      }));

    } else if (name === 'token_amount_pct') {
      if (value === '') {
        setFormData(f => ({ ...f, token_amount_pct: '', token_amount: '' }));
        if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
        return;
      }
      const ba  = Math.round(Number(formData.booking_amount)) || 0;
      const pct = Number(value);
      const safePct = Number.isFinite(pct) ? pct : 0;
      const amt = Math.round((safePct / 100) * ba);
      setFormData(f => ({
        ...f,
        token_amount_pct: value,
        token_amount: amt > 0 ? amt.toString() : ''
      }));

    } else if (name === 'token_amount') {
      if (value === '') {
        setFormData(f => ({ ...f, token_amount: '', token_amount_pct: '' }));
        if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
        return;
      }
      const ba  = Math.round(Number(formData.booking_amount)) || 0;
      const amt = Math.round(Number(value)) || 0;
      const pct = ba > 0 && amt > 0 ? ((amt / ba) * 100).toFixed(1) : '';
      setFormData(f => ({
        ...f,
        token_amount: value,
        token_amount_pct: pct,
      }));

    } else {
      setFormData(f => ({ ...f, [name]: value }));
    }

    if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
  };

  const handleDateChange = (date) => {
    setFormData(f => ({ ...f, booking_date: date }));
    if (errors.booking_date) setErrors(p => ({ ...p, booking_date: '' }));
  };

  // ── validate (original) ───────────────────────────────────────────────────
  const validate = (data) => {
    const errs = {};
    if (!data.booking_date || data.booking_date === null || data.booking_date === undefined) {
      errs.booking_date = 'Booking date is required';
    } else if (!dayjs(data.booking_date).isValid()) {
      errs.booking_date = 'Please enter a valid date';
    }
    if (data.booking_amount === '' || data.booking_amount === null || data.booking_amount === undefined) {
      errs.booking_amount = 'Booking amount is required';
    } else if (Number(data.booking_amount) <= 0) {
      errs.booking_amount = 'Booking amount must be greater than 0';
    }
    const ta = Math.round(Number(data.token_amount))  || 0;
    const ba = Math.round(Number(data.booking_amount)) || 0;
    if (ta <= 0 && ba <= 0) {
      errs.token_amount   = 'Either Token Amount or Booking Amount is required.';
      errs.booking_amount = 'Either Token Amount or Booking Amount is required.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── handleSubmit (original) ───────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate(formData)) return;
    setLoading(true);
    try {
      const payload = {
        booking_date:  formData.booking_date ? formData.booking_date.toISOString() : null,
        booking_amount: parseFloat(formData.booking_amount),
        token_amount:   parseFloat(formData.token_amount),
        other_charges:  formData.other_charges.map(c => ({ name: c.name, amount: c.amount, description: c.description })),
        notes: formData.notes,
      };
      const response = await updateBookingAmount(booking.id, payload);
      if (response.status === 200 || response.status === 201) {
        onSave(response.data || payload);
        onClose();
        openSuccessModal('Success', 'Booking finance details updated successfully.');
      } else {
        openErrorModal('Error', 'Failed to update booking finance details.');
      }
    } catch (error) {
      openErrorModal('Error', error.response?.data?.message || 'Failed to update booking finance details');
    } finally {
      setLoading(false);
    }
  };

  // ── handleClose (original) ────────────────────────────────────────────────
  const handleClose = () => {
    setFormData({ booking_date: null, base_price: '', booking_amount: '', booking_amount_pct: '', token_amount: '', token_amount_pct: '', other_charges: [], total_price: '', subtotal: '', notes: '', tax: 0, taxes: 0 });
    setErrors({});
    onClose();
  };

  // ── Derived display helpers ───────────────────────────────────────────────
  const hasErrors = Object.keys(errors).length > 0;

  return (
    <>
      <style>{`
        @keyframes bfSlideUp {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .bf-body { animation: bfSlideUp 220ms ease both; }
        .bf-body::-webkit-scrollbar { width: 5px; }
        .bf-body::-webkit-scrollbar-thumb { background: #ccddd5; border-radius: 99px; }
        .bf-body::-webkit-scrollbar-track { background: transparent; }
        .bf-input:focus { border-color: ${T.green} !important; box-shadow: 0 0 0 3px rgba(10,107,62,0.10) !important; }
        textarea.bf-input:focus { outline: none; }
        select.bf-input:focus   { outline: none; }
      `}</style>

      {/* ── Main Dialog ──────────────────────────────────────────────────── */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: '16px', overflow: 'hidden', m: 0,
            width: '100%', maxWidth: 560, maxHeight: '94vh',
            boxShadow: '0 24px 80px rgba(0,0,0,0.28)',
            border: `1.5px solid ${T.border}`,
          }
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '94vh' }}>

          {/* ── Header ──────────────────────────────────────────────────── */}
          <div style={{
            background: 'linear-gradient(135deg,#0a6b3e,#0d8a50)',
            padding: '18px 22px', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
              <div style={{
                width: 42, height: 42, borderRadius: 11, flexShrink: 0,
                background: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
              }}>🏦</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', letterSpacing: '-0.3px' }}>
                  {isEdit ? 'Edit Booking Finance' : 'Update Booking Finance'}
                </div>
                {booking && (
                  <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>
                    Managing finance details for booking {booking.booking_id}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={handleClose}
              style={{
                background: 'rgba(255,255,255,0.14)', border: 'none',
                borderRadius: 8, width: 32, height: 32,
                fontSize: 20, color: '#fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, transition: 'background 150ms',
              }}
            >×</button>
          </div>

          {/* ── Scrollable body ──────────────────────────────────────────── */}
          <div
            className="bf-body"
            style={{ 
              overflowY: 'auto',
              padding: '16px 18px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              background: T.bg,
              flex: 1,
              minHeight: 0,
            }}
          >

            {/* 1 ─ Booking Date ──────────────────────────────────────────── */}
            <Section icon="📅" title="Booking Date">
              <div style={{ maxWidth: 230 }}>
                <Field label="Booking Date" required error={errors.booking_date}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={formData.booking_date || null}
                      onChange={handleDateChange}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: {
                          size: 'small',
                          error: !!errors.booking_date,
                          placeholder: 'DD/MM/YYYY',
                          sx: {
                            width: '100%',
                            '& .MuiInputBase-root': { borderRadius: '9px', fontSize: '13px', background: T.white },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: errors.booking_date ? T.red : T.border },
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.green, borderWidth: '1.5px' },
                            '& .MuiFormHelperText-root': { display: 'none' }, // we render error ourselves
                          }
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Field>
              </div>
            </Section>

            {/* 2 ─ Finance Details ────────────────────────────────────────── */}
            <Section icon="🏦" title="Finance Details">
              <Field
                label="Base Price"
                helper={formData.base_price ? `Base price from unit configuration (${formatPriceVerbose(formData.base_price)})` : 'Base price from unit configuration'}
                error={errors.base_price}
              >
                <Input
                  name="base_price" value={formData.base_price}
                  type="number" disabled
                  suffix={formData.base_price ? formatPriceVerbose(formData.base_price) : ''}
                />
              </Field>
            </Section>

            {/* 3 ─ Booking Amount (single title from AmountPair) ───────────── */}
            <Section icon="💳" title="Booking Amount" hideHeader>
              <AmountPair
                icon="💳" title="Booking Amount"
                pctName="booking_amount_pct" pctVal={formData.booking_amount_pct}
                amtName="booking_amount"     amtVal={formData.booking_amount}
                onChange={handleChange}
                pctErr={errors.booking_amount_pct}
                amtErr={errors.booking_amount}
                amtHelper="Enter booking amount"
              />
            </Section>

            {/* 4 ─ Token Amount (single title from AmountPair) ─────────────── */}
            <Section icon="🏷️" title="Token Amount" hideHeader>
              <AmountPair
                icon="🏷️" title="Token Amount"
                pctName="token_amount_pct" pctVal={formData.token_amount_pct}
                amtName="token_amount"     amtVal={formData.token_amount}
                onChange={handleChange}
                pctErr={errors.token_amount_pct}
                amtErr={errors.token_amount}
                amtHelper="Enter token amount"
                onInfo={() => setInfoDialog({
                  open: true,
                  title: 'Token Amount',
                  content: `A token amount is a small initial payment made by the buyer to indicate serious interest in purchasing a specific unit.\n\nAmount: Typically ranges from ₹10,000 to ₹1,00,000, depending on the project.\n\nPurpose: It temporarily reserves the unit for a short duration while documentation, legal checks, or loan processing are underway.\n\nNote: This amount is usually adjusted against the final booking amount. Refundability is subject to the developer's policy.`
                })}
              />
            </Section>

            {/* 5 ─ Other Charges ──────────────────────────────────────────── */}
            <Section icon="➕" title="Other Charges">
              <OtherChargesSection
                charges={formData.other_charges}
                onChange={(charges) => setFormData(f => ({ ...f, other_charges: charges }))}
                otherCharges={Array.isArray(otherCharges) ? otherCharges : (otherCharges && typeof otherCharges === 'object' ? Object.values(otherCharges) : [])}
              />
            </Section>

            {/* 6 ─ Summary ────────────────────────────────────────────────── */}
            <div style={{
              background: T.white, border: `1px solid ${T.border}`,
              borderRadius: 12,
            }}>
              <div style={{ padding: '11px 16px', background: '#f8fbf9', borderBottom: `1px solid ${T.borderL}`, fontSize: 11, fontWeight: 800, color: T.ink2, textTransform: 'uppercase', letterSpacing: '0.55px' }}>
                Summary
              </div>
              <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <SumRow label="Base Price"  value={formatPriceVerbose(formData.base_price)} />

                {formData.other_charges.map((c, i) => (
                  <SumRow key={i} label={`Other Charges (${c.name})`} value={formatPriceVerbose(c.amount)} />
                ))}

                <SumRow label="Subtotal" value={formatPriceVerbose(formData.subtotal)} bold />

                {formData.taxes > 0 && (
                  <SumRow label={`Tax (${formData.tax}%)`} value={`₹${Number(formData.taxes || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                )}

                <div style={{ paddingTop: 8, marginTop: 4, borderTop: `2px solid ${T.border}` }}>
                  <SumRow label="Total:" value={formatPriceVerbose(formData.total_price)} bold green />
                </div>

                <div style={{ fontSize: 10.5, color: T.muted, marginTop: 4, fontStyle: 'italic' }}>
                  Total Price = Base Price + Other Charges
                </div>
              </div>
            </div>

            {/* 7 ─ Booking Amounts Summary (always show above Notes) ───────── */}
            <div style={{
              background: T.blueLt, border: `1.5px solid ${T.blueBdr}`,
              borderRadius: 12, overflow: 'hidden',
            }}>
              <div style={{ padding: '11px 16px', borderBottom: `1px solid ${T.blueBdr}`, fontSize: 11, fontWeight: 800, color: T.blue, textTransform: 'uppercase', letterSpacing: '0.55px' }}>
                Booking Amounts Summary
              </div>
              <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${T.blueBdr}` }}>
                  <span style={{ fontSize: 12.5, color: T.blue, fontWeight: 600 }}>Booking Amount</span>
                  <span style={{ fontSize: 13, color: T.blue, fontWeight: 700 }}>
                    {Number(formData.booking_amount) > 0 ? formatPriceVerbose(formData.booking_amount) : '—'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${T.blueBdr}` }}>
                  <span style={{ fontSize: 12.5, color: T.blue, fontWeight: 700 }}>Token Amount</span>
                  <span style={{ fontSize: 13, color: T.blue, fontWeight: 700 }}>
                    {Number(formData.token_amount) > 0
                      ? `₹${Number(formData.token_amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* 8 ─ Notes ──────────────────────────────────────────────────── */}
            <Section icon="📝" title="Notes">
              <Field label="Notes">
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Additional notes about the booking finance..."
                  rows={3}
                  className="bf-input"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '10px 12px', fontSize: 13, color: T.ink,
                    background: T.white, border: `1.5px solid ${T.border}`,
                    borderRadius: 9, resize: 'vertical', lineHeight: 1.65,
                    transition: 'border-color 150ms',
                  }}
                />
              </Field>
            </Section>
          </div>

          {/* ── Footer ───────────────────────────────────────────────────── */}
          <div style={{
            padding: '13px 18px', borderTop: `1.5px solid ${T.border}`,
            background: '#f8fbf9', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
          }}>
            {/* Left: validation hint */}
            <div style={{ fontSize: 11.5, minWidth: 0 }}>
              {hasErrors && (
                <span style={{ color: T.red, fontWeight: 600 }}>⚠ Please fix the errors above</span>
              )}
            </div>

            {/* Right: actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
              <button
                onClick={handleClose}
                disabled={loading}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: T.redLt, border: `1.5px solid ${T.redBdr}`,
                  borderRadius: 9, padding: '8px 16px',
                  fontSize: 12.5, fontWeight: 700, color: T.red,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1, transition: 'all 150ms',
                }}
              >✕ Cancel</button>

              <button
                onClick={handleSubmit}
                disabled={loading}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: loading
                    ? '#7ab898'
                    : 'linear-gradient(135deg,#0a6b3e,#0d8a50)',
                  border: 'none', borderRadius: 9, padding: '8px 20px',
                  fontSize: 12.5, fontWeight: 700, color: '#fff',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  boxShadow: loading ? 'none' : '0 2px 10px rgba(10,107,62,0.28)',
                  transition: 'all 150ms',
                }}
              >
                {loading ? '⏳ Saving…' : `💾 ${isEdit ? 'Save Changes' : 'Update Finance'}`}
              </button>
            </div>
          </div>
        </div>
      </Dialog>

      {/* ── Snackbar (original) ───────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ── Info modal (replaces nested MUI Dialog) ───────────────────────── */}
      {infoDialog.open && (
        <InfoModal
          title={infoDialog.title}
          content={infoDialog.content}
          onClose={() => setInfoDialog(d => ({ ...d, open: false }))}
        />
      )}
    </>
  );
};

export default AddBookingFinance;
